import { IdsService } from '@/shared/service/utils.service'

import { makeTranslatableText } from '../../../types/TranslatableText.type'
import { type Coords, type POI, type ZOI } from './map.type'

type Position = [number, number]
type Lines = [[Position]]

type GeoJsonFeatures = {
  features: GeoJsonFeature[]
}

type Point = { type: 'Point'; coordinates: Position }
type Polygon = { type: 'Polygon'; coordinates: Lines }
type Geometry = Point | Polygon
type GeoJsonFeature = {
  geometry: Geometry
  properties: {
    name?: string
    nom?: string // specific field for Fimu
    categorie?: string // specific field for Fimu
    color?: string
    image?: string
  }
}

type GeoJsonImportResult = {
  pois: POI[]
  zois: ZOI[]
  errors: string[]
}

function isPoint(geometry: Geometry): geometry is Point {
  return geometry.type === 'Point'
}

function isPolygon(geometry: Geometry): geometry is Polygon {
  return geometry.type === 'Polygon'
}

function toCoords(position: Position): Coords {
  return { lng: position[0], lat: position[1] }
}

function isValidCoords(coords: Coords): boolean {
  return (
    coords.lat >= -90 &&
    coords.lat <= 90 &&
    coords.lng >= -180 &&
    coords.lng <= 180
  )
}

export const GeoJsonParser = {
  parse(
    json: string,
    idGenerator = (): string => IdsService.generateId(),
  ): GeoJsonImportResult {
    const summary: GeoJsonImportResult = { pois: [], zois: [], errors: [] }
    try {
      const result = JSON.parse(json) as GeoJsonFeatures
      result.features
        .filter(({ geometry }) => isPoint(geometry) || isPolygon(geometry))
        .reduce((result, feature) => {
          const featureName =
            feature.properties.name ??
            feature.properties.nom ??
            feature.properties.categorie ??
            ''
          if (isPoint(feature.geometry)) {
            const coords = toCoords(feature.geometry.coordinates)
            if (!isValidCoords(coords)) {
              result.errors.push(
                `Coordonnées invalides pour "${featureName}" (lng: ${coords.lng}, lat: ${coords.lat})`,
              )
              return result
            }
            result.pois.push({
              coords,
              labels: makeTranslatableText(featureName),
              pic: feature.properties.image ?? 'DEFAULT',
              markerIconId: feature.properties.image ?? 'DEFAULT',
              entityType: 'NONE',
              entityId: '',
              oid: idGenerator(),
            })
          }
          if (isPolygon(feature.geometry)) {
            const zoneCorners = feature.geometry.coordinates[0].map(toCoords)
            const invalidCorner = zoneCorners.find((c) => !isValidCoords(c))
            if (invalidCorner) {
              result.errors.push(
                `Coordonnées invalides pour "${featureName}" (lng: ${invalidCorner.lng}, lat: ${invalidCorner.lat})`,
              )
              return result
            }
            result.zois.push({
              zoneCorners,
              color: feature.properties.color ?? '#4CB9DC',
              labels: makeTranslatableText(featureName),
              pic: '',
              entityType: 'NONE',
              entityId: '',
              oid: idGenerator(),
            })
          }
          return result
        }, summary)
    } catch {
      summary.errors.push(
        'Le fichier ne respecte pas le format GeoJson convenu.',
      )
    }
    return summary
  },
}
